import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";

/**
 * Orchestrates the product purchase options toggle group and CTA button.
 *
 * Manages three purchase paths — Standard Sizes, Custom Size, Wall-to-Wall —
 * as a toggle group. Switches the CTA between add-to-cart (standard) and
 * get-a-quote (custom / wall-to-wall) modes.
 *
 * Listens on `document` for `variant:update` events so it works regardless of
 * where the variant picker lives in the DOM (e.g. inside a drawer).
 */
export default class ProductPurchaseOptions extends Component {
  /** @type {"standard" | "custom" | "wtw"} */
  #activeToggle = null;

  #hasVariant = false;

  /** @type {number} */
  #variantPrice = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("click", this.#handleToggleClick);
    // Capture phase so we intercept before add-to-cart-component processes the click
    this.addEventListener("click", this.#handleCtaClick, true);
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);

    const urlHasVariant = new URL(window.location.href).searchParams.has("variant");

    if (this.dataset.defaultToggle) {
      this.#activeToggle = this.dataset.defaultToggle;
    } else if (urlHasVariant) {
      this.#hasVariant = true;
      this.#variantPrice = Number(this.dataset.selectedVariantPrice) || 0;
      this.#activeToggle = "standard";
    }

    this.#updateToggleStates();
    this.#updateCta();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener("click", this.#handleToggleClick);
    this.removeEventListener("click", this.#handleCtaClick, true);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  #handleToggleClick = (event) => {
    const toggle = event.target.closest("[data-toggle]");
    if (!toggle) return;

    const mode = toggle.dataset.toggle;
    if (!mode || mode === this.#activeToggle) return;

    if (mode === "standard") return;

    const previousMode = this.#activeToggle;
    this.#activeToggle = mode;

    // If leaving standard mode, clear the selected variant
    if (previousMode === "standard") {
      this.#resetVariant();
    }

    this.#updateToggleStates();
    this.#updateCta();
  };

  /** @param {import("@theme/events").VariantUpdateEvent} event */
  #handleVariantUpdate = (event) => {
    const { resource, data } = event.detail;
    if (!resource) return;

    if (data.newProduct) {
      this.dataset.productId = data.newProduct.id;
    } else if (data.productId !== this.dataset.productId) {
      return;
    }

    const variantInput = this.querySelector('input[name="id"]');
    if (variantInput) variantInput.value = resource.id;

    this.#hasVariant = true;
    this.#variantPrice = resource.price;
    this.#activeToggle = "standard";
    this.#updateToggleStates();
    this.#updateCta();
  };

  #resetVariant() {
    this.#hasVariant = false;
    this.#variantPrice = 0;

    const variantInput = this.querySelector('input[name="id"]');
    if (variantInput) variantInput.value = "";

    const sizeLabel = this.querySelector("[data-size-label]");
    if (sizeLabel) {
      sizeLabel.textContent = sizeLabel.dataset.defaultText ?? "";
    }

    // Reset the variant picker in the drawer
    const picker = document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    if (picker && typeof picker.reset === "function") {
      picker.reset();
    }

    // Clean variant from URL
    const url = new URL(window.location.href);
    if (url.searchParams.has("variant")) {
      url.searchParams.delete("variant");
      history.replaceState({}, "", url.toString());
    }
  }

  #updateToggleStates() {
    const toggles = this.querySelectorAll("[data-toggle]");
    for (const toggle of toggles) {
      const isActive = toggle.dataset.toggle === this.#activeToggle;
      toggle.setAttribute("aria-pressed", String(isActive));
    }
  }

  #updateCta() {
    const form = this.querySelector("product-form-component");
    const quoteLink = this.querySelector("[data-quote-link]");
    const whereToBuy = this.querySelector("[data-where-to-buy]");

    const isQuoteMode = this.#activeToggle === "custom" || this.#activeToggle === "wtw";

    if (form) {
      form.classList.toggle("hidden", isQuoteMode);

      if (!isQuoteMode) {
        const button = form.querySelector('button[name="add"]');
        const textEl = form.querySelector("[data-cta-text]");
        if (button && textEl) {
          if (this.#hasVariant && this.#variantPrice > 0) {
            textEl.textContent = this.dataset.textAddToCart;
            button.disabled = false;
          } else {
            textEl.textContent = this.dataset.textSelect;
            button.disabled = true;
          }
        }
      }
    }

    if (whereToBuy) {
      whereToBuy.classList.toggle("hidden", isQuoteMode);
      whereToBuy.classList.toggle("flex", !isQuoteMode);
    }

    if (quoteLink) {
      quoteLink.classList.toggle("hidden", !isQuoteMode);
      quoteLink.classList.toggle("flex", isQuoteMode);

      if (isQuoteMode) {
        const url = this.#activeToggle === "custom" ? this.dataset.customUrl : this.dataset.wtwUrl;
        quoteLink.href = url;
      }
    }
  }

  /**
   * Capture-phase listener that intercepts CTA clicks in quote mode,
   * preventing the add-to-cart-component from processing them.
   */
  #handleCtaClick = (event) => {
    const button = event.target.closest('button[name="add"]');
    if (!button) return;

    const quoteMode = button.dataset.quoteMode;
    if (!quoteMode) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const url = quoteMode === "custom" ? this.dataset.customUrl : this.dataset.wtwUrl;

    if (url) {
      window.location.href = url;
    }
  };
}

if (!customElements.get("product-purchase-options")) {
  customElements.define("product-purchase-options", ProductPurchaseOptions);
}
